/** @format */

import { useTranslation } from "react-i18next";
import EllipsisIcon from "../assets/icons/Ellipsis.svg?react";

interface PaginationProps {
	total: number;
	offset: number;
	limit: number;
	onPageChange: (offset: number) => void;
}

const getBtnStyle = (isActive: boolean) =>
	isActive
		? "h-8 w-10 flex justify-center items-center bg-(--public-color-theme-primary) border border-(--public-color-theme-primary) cursor-pointer text-[14px] font-roboto text-white"
		: "h-8 w-10 flex justify-center items-center border border-(--public-color-border-filter) cursor-pointer text-[14px] font-roboto text-(--public-color-theme-primary)";

const getArrowBtnStyle = (isDisabled: boolean) =>
	isDisabled
		? "h-8 w-10 border border-(--public-color-border-filter) text-gray-300 cursor-not-allowed"
		: "h-8 w-10 border border-(--public-color-border-filter) text-(--public-color-theme-primary) cursor-pointer";

export const Pagination = ({ total, offset, limit, onPageChange }: PaginationProps) => {
	const { t } = useTranslation();

	const totalPages = Math.ceil(total / limit);
	const firstPage = 1;
	const lastPage = totalPages;
	const currentPage = Math.floor(offset / limit) + 1;
	if (totalPages <= 1) {
		return null;
	}

	const PAGES_AROUND_CURRENT_PAGE = 2;

	const windowStart = Math.max(firstPage + 1, currentPage - PAGES_AROUND_CURRENT_PAGE);
	const windowEnd = Math.min(lastPage - 1, currentPage + PAGES_AROUND_CURRENT_PAGE);
	const middlePages = Array.from({ length: windowEnd - windowStart + 1 }, (_, i) => windowStart + i);

	const canShowLeftEllipsis = windowStart > firstPage + 1;
	const canShowRightEllipsis = windowEnd < lastPage - 1;

	const goToPage = (page: number) => onPageChange((page - 1) * limit);

	const renderPageButton = (page: number) => (
		<button
			key={page}
			aria-current={page === currentPage ? "page" : undefined}
			aria-label={t("pagination.page", { page })}
			type="button"
			onClick={() => goToPage(page)}
			className={getBtnStyle(page === currentPage)}
		>
			{page}
		</button>
	);
	return (
		<nav
			aria-label={t("pagination.pagination")}
			className="inline-flex h-8 mt-1 items-center justify-between bg-white rounded-[10px]"
		>
			<button
				aria-label={t("pagination.previousPage")}
				type="button"
				disabled={offset === 0}
				onClick={() => onPageChange(Math.max(0, offset - limit))}
				className={`${getArrowBtnStyle(offset === 0)} flex items-center justify-between rounded-tl-lg rounded-bl-lg`}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth={2}
					stroke="currentColor"
					className="size-4 mx-auto"
				>
					<path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
				</svg>
			</button>

			{renderPageButton(firstPage)}

			{canShowLeftEllipsis && (
				<button
					type="button"
					className="h-8 w-10 flex justify-center items-center text-gray-300 border border-(--public-color-border-filter)"
				>
					<EllipsisIcon />
				</button>
			)}

			{middlePages.map(renderPageButton)}

			{canShowRightEllipsis && (
				<button
					type="button"
					className="h-8 w-10 flex justify-center items-center text-gray-300 border border-(--public-color-border-filter)"
				>
					<EllipsisIcon />
				</button>
			)}

			{renderPageButton(lastPage)}

			<button
				aria-label={t("pagination.nextPage")}
				type="button"
				disabled={offset + limit >= total}
				onClick={() => onPageChange(offset + limit)}
				className={`${getArrowBtnStyle(offset + limit >= total)} flex items-center justify-between rounded-tr-lg rounded-br-lg`}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth={2}
					stroke="currentColor"
					className="size-4 mx-auto"
				>
					<path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
				</svg>
			</button>
		</nav>
	);
};
